import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  AppBar,
  Toolbar,
  Divider,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import * as MuiIcons from '@mui/icons-material';
import {
  SwipeRightAlt as SwipeIcon,
  Schedule as ScheduleIcon,
  Refresh as RefreshIcon,
  Home as HomeIcon,
  SearchOff as NotFoundIcon,
  ErrorOutline as ErrorIcon,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { boardsApi, sportsApi } from '../api';
import { useAuth } from '../hooks';
import type { TeamMapping } from '../types';
import { ThemeWrapper } from '../components/theme/ThemeWrapper';
import { BoardExport } from '../components/BoardExport';
import { LiveGameBanner } from '../components/board';
import type { Board, SquareWithNumbers, Hit, NumberMode, BoxNeighborSize, CellMode, CellShape } from '../types';
import { DEFAULT_THEME_DATA, getNumberSetKeys } from '../types';
import {
  getMergedGridDimensions,
  getFirstSquareForVisualPosition,
  getSquarePositionsForVisualCell,
  getMergedCellSize,
  getNumbersForMergedHeaderCell,
  getCellNumberForSquarePosition,
} from '../utils/gridUtils';
import { stripHtmlTags } from '@katapult/ui';
import logoSvg from '../assets/logo.svg';
import kLogoSvg from '../assets/k-logo.svg';

// Get box border style for a cell based on its position within a box group
// Uses inset box-shadow to avoid conflicting with cell borders or Paper elevation
const getBoxBorderStyle = (
  row: number,
  col: number,
  boxSize: BoxNeighborSize,
  borderColor: string,
  borderWidth: number
): React.CSSProperties => {
  let isTopEdge: boolean;
  let isBottomEdge: boolean;
  let isLeftEdge: boolean;
  let isRightEdge: boolean;

  if (boxSize === '2x2') {
    // 2x2 boxes: divide into 5x5 grid of boxes
    isTopEdge = row % 2 === 0;
    isBottomEdge = row % 2 === 1;
    isLeftEdge = col % 2 === 0;
    isRightEdge = col % 2 === 1;
  } else if (boxSize === '5x5') {
    // 5x5 quadrants: 4 quadrants (top-left, top-right, bottom-left, bottom-right)
    isTopEdge = row % 5 === 0;
    isBottomEdge = row % 5 === 4;
    isLeftEdge = col % 5 === 0;
    isRightEdge = col % 5 === 4;
  } else {
    // 5x2 boxes: 2 columns of 5-wide boxes, 5 rows of 2-tall boxes
    isTopEdge = row % 2 === 0;
    isBottomEdge = row % 2 === 1;
    isLeftEdge = col % 5 === 0;
    isRightEdge = col % 5 === 4;
  }

  // Build inset box-shadow for each edge that needs a border
  // Using box-shadow avoids conflicts with existing cell borders
  const shadows: string[] = [];
  if (isTopEdge) {
    shadows.push(`inset 0 ${borderWidth}px 0 0 ${borderColor}`);
  }
  if (isBottomEdge) {
    shadows.push(`inset 0 -${borderWidth}px 0 0 ${borderColor}`);
  }
  if (isLeftEdge) {
    shadows.push(`inset ${borderWidth}px 0 0 0 ${borderColor}`);
  }
  if (isRightEdge) {
    shadows.push(`inset -${borderWidth}px 0 0 0 ${borderColor}`);
  }

  if (shadows.length === 0) {
    return {};
  }

  return {
    boxShadow: shadows.join(', '),
  };
};

// Helper to get contrasting text color based on background
const getContrastColor = (bgColor: string): string => {
  const hex = bgColor.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5 ? '#000000' : '#ffffff';
};

// Helper to render an icon by name
const renderIcon = (name: string, size: number = 18) => {
  const IconComponent = (MuiIcons as Record<string, React.ComponentType<{ sx?: object }>>)[name];
  if (!IconComponent) return null;
  return <IconComponent sx={{ fontSize: size }} />;
};

// Get quarter key label (Q1, 1H, P1, etc.)
const getQuarterKeyLabel = (key: string, quarters: number): string => {
  if (quarters === 2) {
    if (key === '1H') return '1st Half';
    if (key === '2H') return '2nd Half';
  }
  if (quarters === 3) {
    if (key === 'P1') return '1st Period';
    if (key === 'P2') return '2nd Period';
    if (key === 'P3') return '3rd Period';
  }
  if (key === 'Q1') return 'Q1';
  if (key === 'Q2') return 'Q2 / Halftime';
  if (key === 'Q3') return 'Q3';
  if (key === 'Q4') return 'Q4 / Final';
  return key;
};

// Get ordered quarter keys based on number of quarters
const getOrderedQuarterKeys = (quarters: number): string[] => {
  if (quarters === 2) return ['1H', '2H'];
  if (quarters === 3) return ['P1', 'P2', 'P3'];
  return ['Q1', 'Q2', 'Q3', 'Q4'];
};

// Convert flat squares array to 2D grid
const squaresToGrid = (squares: SquareWithNumbers[]): SquareWithNumbers[][] => {
  const grid: SquareWithNumbers[][] = Array.from({ length: 10 }, () => Array(10).fill(null));
  squares.forEach((square) => {
    if (square.row >= 0 && square.row < 10 && square.col >= 0 && square.col < 10) {
      grid[square.row][square.col] = square;
    }
  });
  return grid;
};

export const PublicBoardViewPage: React.FC = () => {
  const { boardSlug } = useParams<{ boardSlug: string }>();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [board, setBoard] = useState<Board | null>(null);
  const [squares, setSquares] = useState<SquareWithNumbers[]>([]);
  const [hits, setHits] = useState<Hit[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [errorType, setErrorType] = useState<'not_found' | 'not_published' | 'other' | null>(null);
  const [showScrollHint, setShowScrollHint] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [teamMappings, setTeamMappings] = useState<TeamMapping[]>([]);

  // Fetch board data
  const fetchBoardData = useCallback(async () => {
    if (!boardSlug) return;

    setLoading(true);
    setError(null);
    setErrorType(null);

    try {
      const [boardData, squaresData, hitsData] = await Promise.all([
        boardsApi.getPublic(boardSlug),
        boardsApi.getPublicSquares(boardSlug),
        boardsApi.getPublicHits(boardSlug),
      ]);

      setBoard(boardData);
      setSquares(squaresData);
      setHits(hitsData);
    } catch (err: unknown) {
      const apiError = err as { status?: number; message?: string };
      if (apiError.status === 404) {
        setError('Board not found');
        setErrorType('not_found');
      } else if (apiError.status === 403) {
        setError('This board is not published yet');
        setErrorType('not_published');
      } else {
        setError(apiError.message || 'Failed to load board');
        setErrorType('other');
      }
    } finally {
      setLoading(false);
    }
  }, [boardSlug]);

  useEffect(() => {
    fetchBoardData();
  }, [fetchBoardData]);

  // Fetch team mappings for live game boards
  useEffect(() => {
    if (!board?.externalGameId) return;
    let mounted = true;
    sportsApi.getGame(board.externalGameId).then(data => {
      if (mounted) setTeamMappings(data.teamMappings || []);
    }).catch(() => {});
    return () => { mounted = false; };
  }, [board?.externalGameId]);

  // Check if horizontal scroll is needed and show hint on mobile
  useEffect(() => {
    if (!isMobile || loading || error || !board) return;

    const checkScroll = () => {
      const container = scrollContainerRef.current;
      if (container && container.scrollWidth > container.clientWidth) {
        // Check if user hasn't already scrolled or dismissed
        const hintDismissed = sessionStorage.getItem('scrollHintDismissed');
        if (!hintDismissed) {
          setShowScrollHint(true);
          // Auto-hide after 3 seconds
          const timer = setTimeout(() => {
            setShowScrollHint(false);
          }, 3000);
          return () => clearTimeout(timer);
        }
      }
    };

    // Check after a short delay to ensure layout is complete
    const timer = setTimeout(checkScroll, 500);
    return () => clearTimeout(timer);
  }, [isMobile, loading, error, board]);

  // Handle scroll to dismiss hint
  const handleScroll = useCallback(() => {
    if (showScrollHint) {
      setShowScrollHint(false);
      sessionStorage.setItem('scrollHintDismissed', 'true');
    }
  }, [showScrollHint]);

  // Convert squares to grid format
  const grid = useMemo(() => squaresToGrid(squares), [squares]);

  // Theme data - merge with defaults so missing properties fall back to defaults
  const rawThemeData = board?.theme?.themeData;
  const themeData = rawThemeData ? { ...DEFAULT_THEME_DATA, ...rawThemeData } : DEFAULT_THEME_DATA;
  const hasTheme = Boolean(board?.theme);

  // Hit counts per square
  const hitCounts = useMemo(() => {
    const counts = new Map<string, number>();
    hits.forEach((h) => {
      const key = `${h.winningRow}-${h.winningCol}`;
      counts.set(key, (counts.get(key) || 0) + 1);
    });
    return counts;
  }, [hits]);

  const getHitCount = (row: number, col: number): number => {
    return hitCounts.get(`${row}-${col}`) || 0;
  };

  const getSquareColor = (square: SquareWithNumbers | undefined, row: number, col: number) => {
    if (!square) return themeData.squareUnclaimed;
    const hitCount = getHitCount(row, col);
    if (hitCount > 0) return themeData.squareWinner;
    return themeData.cellBackground;
  };

  // Determine if names should be hidden (hideNames enabled and board is not locked/completed)
  const shouldHideNames = board?.hideNames && (board?.status === 'draft' || board?.status === 'live');

  // Cell mode settings
  const cellMode = (board?.cellMode || 'standard') as CellMode;
  const cellShape = (board?.cellShape || '1x1') as CellShape;
  const { rows: visualRows, cols: visualCols, rowSpan, colSpan } = getMergedGridDimensions(cellMode, cellShape);

  const getSquareLabel = (square: SquareWithNumbers | undefined, row: number, col: number): { icon?: React.ReactNode; handle?: string } => {
    if (!square) return {};
    if (square.label) return { handle: square.label };

    // If hideNames is enabled and board is live, don't show player info for claimed squares
    if (shouldHideNames && (square.offlinePlayer || square.owner || square.ownerId || square.offlinePlayerId)) {
      return {}; // Return empty - no icon or handle shown
    }

    if (square.offlinePlayer) {
      const icon = square.offlinePlayer.iconName ? renderIcon(square.offlinePlayer.iconName, 16) : null;
      const handle = square.offlinePlayer.handle?.toUpperCase() || square.offlinePlayer.displayName.substring(0, 3).toUpperCase();
      return { icon: icon || undefined, handle };
    }

    if (square.owner) {
      const icon = square.owner.iconName ? renderIcon(square.owner.iconName, 16) : null;
      const handle = square.owner.handle?.toUpperCase() || square.owner.displayName.substring(0, 3).toUpperCase();
      return { icon: icon || undefined, handle };
    }

    // Show cell number for unassigned cells (accounts for cell modes)
    const cellNumber = getCellNumberForSquarePosition(row, col, cellMode, cellShape);
    return { handle: cellNumber.toString() };
  };

  // Format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        <AppBar position="static">
          <Toolbar>
            <Box
              component="img"
              src={logoSvg}
              alt="SquareUp"
              sx={{ height: 32, cursor: 'pointer', mr: 2 }}
              onClick={() => navigate('/')}
            />
            <Box sx={{ flexGrow: 1 }} />
            {isAuthenticated ? (
              <Button color="inherit" onClick={() => navigate('/boards')}>
                My Boards
              </Button>
            ) : (
              <>
                <Button color="inherit" onClick={() => navigate('/login')}>
                  Login
                </Button>
                <Button color="inherit" onClick={() => navigate('/register')}>
                  Register
                </Button>
              </>
            )}
          </Toolbar>
        </AppBar>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  if (error || !board) {
    // Special UI for unpublished boards
    if (errorType === 'not_published') {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
          <AppBar position="static">
            <Toolbar>
              <Box
                component="img"
                src={logoSvg}
                alt="SquareUp"
                sx={{ height: 32, cursor: 'pointer', mr: 2 }}
                onClick={() => navigate('/')}
              />
              <Box sx={{ flexGrow: 1 }} />
              {isAuthenticated ? (
                <Button color="inherit" onClick={() => navigate('/boards')}>
                  My Boards
                </Button>
              ) : (
                <>
                  <Button color="inherit" onClick={() => navigate('/login')}>
                    Login
                  </Button>
                  <Button color="inherit" onClick={() => navigate('/register')}>
                    Register
                  </Button>
                </>
              )}
            </Toolbar>
          </AppBar>
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              px: 2,
              py: { xs: 4, sm: 6 },
              background: (theme) =>
                theme.palette.mode === 'light'
                  ? 'linear-gradient(135deg, #f5f7fa 0%, #e4e8ec 100%)'
                  : 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
            }}
          >
            <Paper
              elevation={3}
              sx={{
                maxWidth: 480,
                width: '100%',
                textAlign: 'center',
                p: { xs: 3, sm: 5 },
                borderRadius: 3,
              }}
            >
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  backgroundColor: 'primary.light',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 3,
                }}
              >
                <ScheduleIcon sx={{ fontSize: 40, color: 'primary.main' }} />
              </Box>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 1.5 }}>
                Board Coming Soon
              </Typography>
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ mb: 3, lineHeight: 1.6 }}
              >
                This board hasn't been published yet. The organizer is still setting things up.
                Check back later or contact them for more details.
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: 2,
                  justifyContent: 'center',
                }}
              >
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={() => fetchBoardData()}
                  sx={{ minWidth: 140 }}
                >
                  Try Again
                </Button>
                <Button
                  variant="contained"
                  startIcon={<HomeIcon />}
                  onClick={() => navigate('/')}
                  sx={{ minWidth: 140 }}
                >
                  Go Home
                </Button>
              </Box>
            </Paper>
          </Box>
          {/* Footer */}
          <Box
            component="footer"
            sx={{
              py: 2,
              px: 2,
              backgroundColor: (theme) =>
                theme.palette.mode === 'light' ? theme.palette.grey[200] : theme.palette.grey[800],
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                component="img"
                src={logoSvg}
                alt="SquareUp"
                sx={{ height: 20, opacity: 0.7 }}
              />
              <Typography variant="body2" sx={{ color: 'text.secondary', opacity: 0.7 }}>
                by
              </Typography>
              <Box
                component="a"
                href="https://katapultmedia.com/"
                target="_blank"
                rel="noopener noreferrer"
                sx={{ display: 'flex', alignItems: 'center' }}
              >
                <Box
                  component="img"
                  src={kLogoSvg}
                  alt="Katapult"
                  sx={{ height: 20, opacity: 0.7, '&:hover': { opacity: 1 } }}
                />
              </Box>
            </Box>
          </Box>
        </Box>
      );
    }

    // UI for 404 not found
    if (errorType === 'not_found') {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
          <AppBar position="static">
            <Toolbar>
              <Box
                component="img"
                src={logoSvg}
                alt="SquareUp"
                sx={{ height: 32, cursor: 'pointer', mr: 2 }}
                onClick={() => navigate('/')}
              />
              <Box sx={{ flexGrow: 1 }} />
              {isAuthenticated ? (
                <Button color="inherit" onClick={() => navigate('/boards')}>
                  My Boards
                </Button>
              ) : (
                <>
                  <Button color="inherit" onClick={() => navigate('/login')}>
                    Login
                  </Button>
                  <Button color="inherit" onClick={() => navigate('/register')}>
                    Register
                  </Button>
                </>
              )}
            </Toolbar>
          </AppBar>
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              px: 2,
              py: { xs: 4, sm: 6 },
              background: (theme) =>
                theme.palette.mode === 'light'
                  ? 'linear-gradient(135deg, #f5f7fa 0%, #e4e8ec 100%)'
                  : 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
            }}
          >
            <Paper
              elevation={3}
              sx={{
                maxWidth: 480,
                width: '100%',
                textAlign: 'center',
                p: { xs: 3, sm: 5 },
                borderRadius: 3,
              }}
            >
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  backgroundColor: 'grey.200',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 3,
                }}
              >
                <NotFoundIcon sx={{ fontSize: 40, color: 'grey.500' }} />
              </Box>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 1.5 }}>
                Board Not Found
              </Typography>
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ mb: 3, lineHeight: 1.6 }}
              >
                We couldn't find a board at this link. It may have been deleted, or the link might be incorrect.
                Double-check the URL or ask the organizer for the correct link.
              </Typography>
              <Button
                variant="contained"
                startIcon={<HomeIcon />}
                onClick={() => navigate('/')}
                sx={{ minWidth: 140 }}
              >
                Go Home
              </Button>
            </Paper>
          </Box>
          {/* Footer */}
          <Box
            component="footer"
            sx={{
              py: 2,
              px: 2,
              backgroundColor: (theme) =>
                theme.palette.mode === 'light' ? theme.palette.grey[200] : theme.palette.grey[800],
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                component="img"
                src={logoSvg}
                alt="SquareUp"
                sx={{ height: 20, opacity: 0.7 }}
              />
              <Typography variant="body2" sx={{ color: 'text.secondary', opacity: 0.7 }}>
                by
              </Typography>
              <Box
                component="a"
                href="https://katapultmedia.com/"
                target="_blank"
                rel="noopener noreferrer"
                sx={{ display: 'flex', alignItems: 'center' }}
              >
                <Box
                  component="img"
                  src={kLogoSvg}
                  alt="Katapult"
                  sx={{ height: 20, opacity: 0.7, '&:hover': { opacity: 1 } }}
                />
              </Box>
            </Box>
          </Box>
        </Box>
      );
    }

    // Default error UI for other/generic errors
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        <AppBar position="static">
          <Toolbar>
            <Box
              component="img"
              src={logoSvg}
              alt="SquareUp"
              sx={{ height: 32, cursor: 'pointer', mr: 2 }}
              onClick={() => navigate('/')}
            />
            <Box sx={{ flexGrow: 1 }} />
            {isAuthenticated ? (
              <Button color="inherit" onClick={() => navigate('/boards')}>
                My Boards
              </Button>
            ) : (
              <>
                <Button color="inherit" onClick={() => navigate('/login')}>
                  Login
                </Button>
                <Button color="inherit" onClick={() => navigate('/register')}>
                  Register
                </Button>
              </>
            )}
          </Toolbar>
        </AppBar>
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            px: 2,
            py: { xs: 4, sm: 6 },
            background: (theme) =>
              theme.palette.mode === 'light'
                ? 'linear-gradient(135deg, #f5f7fa 0%, #e4e8ec 100%)'
                : 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
          }}
        >
          <Paper
            elevation={3}
            sx={{
              maxWidth: 480,
              width: '100%',
              textAlign: 'center',
              p: { xs: 3, sm: 5 },
              borderRadius: 3,
            }}
          >
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                backgroundColor: 'error.light',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 3,
              }}
            >
              <ErrorIcon sx={{ fontSize: 40, color: 'error.main' }} />
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 1.5 }}>
              Something Went Wrong
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ mb: 3, lineHeight: 1.6 }}
            >
              We had trouble loading this board. This might be a temporary issue.
              Please try again in a moment.
            </Typography>
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 2,
                justifyContent: 'center',
              }}
            >
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={() => fetchBoardData()}
                sx={{ minWidth: 140 }}
              >
                Try Again
              </Button>
              <Button
                variant="contained"
                startIcon={<HomeIcon />}
                onClick={() => navigate('/')}
                sx={{ minWidth: 140 }}
              >
                Go Home
              </Button>
            </Box>
          </Paper>
        </Box>
        {/* Footer */}
        <Box
          component="footer"
          sx={{
            py: 2,
            px: 2,
            backgroundColor: (theme) =>
              theme.palette.mode === 'light' ? theme.palette.grey[200] : theme.palette.grey[800],
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              component="img"
              src={logoSvg}
              alt="SquareUp"
              sx={{ height: 20, opacity: 0.7 }}
            />
            <Typography variant="body2" sx={{ color: 'text.secondary', opacity: 0.7 }}>
              by
            </Typography>
            <Box
              component="a"
              href="https://katapultmedia.com/"
              target="_blank"
              rel="noopener noreferrer"
              sx={{ display: 'flex', alignItems: 'center' }}
            >
              <Box
                component="img"
                src={kLogoSvg}
                alt="Katapult"
                sx={{ height: 20, opacity: 0.7, '&:hover': { opacity: 1 } }}
              />
            </Box>
          </Box>
        </Box>
      </Box>
    );
  }

  // Team positioning
  const homeOnTop = board.homeOnTop ?? true;
  const topTeam = homeOnTop ? board.homeTeam : board.awayTeam;
  const leftTeam = homeOnTop ? board.awayTeam : board.homeTeam;
  const topNumbers = homeOnTop ? board.homeNumbers : board.awayNumbers;
  const leftNumbers = homeOnTop ? board.awayNumbers : board.homeNumbers;

  // Multi-number mode helpers
  const numberMode: NumberMode = board.numberMode || 'single';
  const numberSetKeys = getNumberSetKeys(board.quarters || 4, numberMode);
  const hasMultipleNumberSets = numberSetKeys.length > 0;

  const getTopNumbersForKey = (key: string): number[] => {
    if (homeOnTop) {
      return board.quarterHomeNumbers?.[key] ?? board.homeNumbers;
    }
    return board.quarterAwayNumbers?.[key] ?? board.awayNumbers;
  };

  const getLeftNumbersForKey = (key: string): number[] => {
    if (homeOnTop) {
      return board.quarterAwayNumbers?.[key] ?? board.awayNumbers;
    }
    return board.quarterHomeNumbers?.[key] ?? board.homeNumbers;
  };

  // Get a short label for number set keys (for rendering in the grid)
  const getShortKeyLabel = (key: string): string => {
    // Return the key as-is for short labels like Q1, 1H, P1
    return key;
  };

  // Resolve team mappings for live game boards
  const homeTeamMapping = teamMappings.find(t => t.teamName === board.homeTeam || t.abbreviation === board.homeTeam);
  const awayTeamMapping = teamMappings.find(t => t.teamName === board.awayTeam || t.abbreviation === board.awayTeam);
  const topTeamLogoUrl = homeOnTop ? homeTeamMapping?.logoUrl : awayTeamMapping?.logoUrl;
  const leftTeamLogoUrl = homeOnTop ? awayTeamMapping?.logoUrl : homeTeamMapping?.logoUrl;

  // For live boards with classic/no theme, use actual team colors from mappings
  const isClassicOrNoTheme = !board.themeId || board.theme?.lookupKey === 'classic';
  const liveHomeColor = isClassicOrNoTheme && homeTeamMapping?.primaryColor ? homeTeamMapping.primaryColor : undefined;
  const liveAwayColor = isClassicOrNoTheme && awayTeamMapping?.primaryColor ? awayTeamMapping.primaryColor : undefined;

  const homeColor = liveHomeColor || (board.themeId ? themeData.homeTeamColor : (board.homeColor || themeData.homeTeamColor));
  const awayColor = liveAwayColor || (board.themeId ? themeData.awayTeamColor : (board.awayColor || themeData.awayTeamColor));
  const topColor = homeOnTop ? homeColor : awayColor;
  const leftColor = homeOnTop ? awayColor : homeColor;

  // Responsive cell sizes - larger for public view since we have more real estate
  const cellSize = { xs: 36, sm: 46, md: 52 };
  const headerSize = { xs: 28, sm: 34, md: 40 };
  const gap = 2; // Gap between cells

  // Calculate merged cell sizes for responsive breakpoints
  const mergedCellSize = {
    xs: getMergedCellSize(cellSize.xs, gap, cellMode, cellShape),
    sm: getMergedCellSize(cellSize.sm, gap, cellMode, cellShape),
    md: getMergedCellSize(cellSize.md, gap, cellMode, cellShape),
  };

  // Calculate grid cells width for responsive team name centering
  const gridCellsWidth = {
    xs: cellMode !== 'standard' ? mergedCellSize.xs.width * visualCols + gap * (visualCols - 1) : cellSize.xs * 10 + gap * 9,
    sm: cellMode !== 'standard' ? mergedCellSize.sm.width * visualCols + gap * (visualCols - 1) : cellSize.sm * 10 + gap * 9,
    md: cellMode !== 'standard' ? mergedCellSize.md.width * visualCols + gap * (visualCols - 1) : cellSize.md * 10 + gap * 9,
  };

  // Payout info
  const totalPot = board.pricePerSquare * (board.totalCells || 100);
  const profitPercent = board.payoutConfig?.profitPercent ?? 10;
  const profitAmount = totalPot * (profitPercent / 100);
  const payoutPot = totalPot - profitAmount;
  const quarterPayouts = board.payoutConfig?.quarterPayouts || {};
  const quarterWrongWayPayouts = board.payoutConfig?.quarterWrongWayPayouts;
  const quarterPlusNPayouts = board.payoutConfig?.quarterPlusNPayouts;
  const customCategories = board.payoutConfig?.customCategories;
  const orderedQuarterKeys = getOrderedQuarterKeys(board.quarters || 4);

  // Text colors based on theme
  const isLightText = themeData.textColorMode === 'light';
  const textPrimary = isLightText ? '#ffffff' : '#000000';
  const textSecondary = isLightText ? 'rgba(255,255,255,0.85)' : '#666666';
  const tableBorder = isLightText ? 'rgba(255,255,255,0.3)' : '#e0e0e0';
  const tableHeader = isLightText ? 'rgba(255,255,255,0.15)' : '#f5f5f5';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {/* Header */}
      <AppBar position="static">
        <Toolbar>
          <Box
            component="img"
            src={logoSvg}
            alt="SquareUp"
            sx={{ height: 32, cursor: 'pointer', mr: 2 }}
            onClick={() => navigate('/')}
          />
          <Box sx={{ flexGrow: 1 }} />
          {isAuthenticated ? (
            <Button color="inherit" onClick={() => navigate('/boards')}>
              My Boards
            </Button>
          ) : (
            <>
              <Button color="inherit" onClick={() => navigate('/login')}>
                Login
              </Button>
              <Button color="inherit" onClick={() => navigate('/register')}>
                Register
              </Button>
            </>
          )}
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Box sx={{ flex: 1, py: { xs: 2, sm: 3 }, px: { xs: 1, sm: 2 }, display: 'flex', justifyContent: 'center' }}>
        <Box sx={{ maxWidth: 'lg', width: '100%' }}>
          <ThemeWrapper themeData={board.theme?.themeData} showAnimations={true}>
            <Box sx={{ p: { xs: 2, sm: 3 } }}>
            {/* Board Header */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: textPrimary, mb: 1 }}>
                {board.name}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
                <Typography variant="body1" sx={{ color: textSecondary }}>
                  {formatDate(board.gameDate)}
                </Typography>
                <Typography variant="body2" sx={{ color: textSecondary }}>
                  ${board.pricePerSquare}/square
                </Typography>
                <Typography variant="body2" sx={{ color: textSecondary }}>
                  Total: ${totalPot.toFixed(0)} | Payout: ${payoutPot.toFixed(0)}
                </Typography>
              </Box>
              {board.description && (
                <Typography variant="body2" sx={{ color: textSecondary, mt: 1, fontStyle: 'italic' }}>
                  {stripHtmlTags(board.description).substring(0, 300)}
                  {stripHtmlTags(board.description).length > 300 ? '...' : ''}
                </Typography>
              )}
            </Box>

            {/* Live Game Banner */}
            {board.externalGameId && (
              <LiveGameBanner externalGameId={board.externalGameId} sport={board.sport} />
            )}

            {/* Grid and Info Layout */}
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
              {/* Grid Section - allow horizontal scroll on mobile */}
              <Box
                ref={scrollContainerRef}
                onScroll={handleScroll}
                sx={{ flex: '1 1 auto', display: 'flex', justifyContent: { xs: 'flex-start', sm: 'center' }, overflowX: 'auto', position: 'relative' }}
              >
                {/* Scroll hint overlay for mobile */}
                {showScrollHint && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      right: 0,
                      bottom: 0,
                      width: 60,
                      background: 'linear-gradient(to right, transparent, rgba(0,0,0,0.4))',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      zIndex: 10,
                      pointerEvents: 'none',
                      animation: 'pulse 1.5s ease-in-out infinite',
                      '@keyframes pulse': {
                        '0%, 100%': { opacity: 1 },
                        '50%': { opacity: 0.5 },
                      },
                    }}
                  >
                    <SwipeIcon
                      sx={{
                        color: 'white',
                        fontSize: 32,
                        animation: 'swipe 1s ease-in-out infinite',
                        '@keyframes swipe': {
                          '0%, 100%': { transform: 'translateX(0)' },
                          '50%': { transform: 'translateX(8px)' },
                        },
                      }}
                    />
                  </Box>
                )}
                <Box sx={{ display: 'inline-block', minWidth: 'max-content' }}>
                  {/* Top Team Name */}
                  <Box sx={{
                    display: 'flex',
                    mb: 0.5,
                    ml: hasMultipleNumberSets
                      ? { xs: `${headerSize.xs + (numberSetKeys.length * (headerSize.xs + 2))}px`, sm: `${headerSize.sm + (numberSetKeys.length * (headerSize.sm + 2))}px`, md: `${headerSize.md + (numberSetKeys.length * (headerSize.md + 2))}px` }
                      : { xs: `${headerSize.xs + headerSize.xs}px`, sm: `${headerSize.sm + headerSize.sm}px`, md: `${headerSize.md + headerSize.md}px` }
                  }}>
                    <Box
                      sx={{
                        width: { xs: gridCellsWidth.xs + 20, sm: gridCellsWidth.sm + 20, md: gridCellsWidth.md + 20 },
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1,
                      }}
                    >
                      {topTeamLogoUrl && (
                        <Box
                          component="img"
                          src={topTeamLogoUrl}
                          alt={topTeam}
                          onError={(e: React.SyntheticEvent<HTMLImageElement>) => { e.currentTarget.style.display = 'none'; }}
                          sx={{ width: { xs: 20, sm: 26, md: 28 }, height: { xs: 20, sm: 26, md: 28 }, objectFit: 'contain', flexShrink: 0 }}
                        />
                      )}
                      <Typography
                        sx={{
                          fontWeight: 'bold',
                          color: topColor,
                          fontSize: { xs: '0.85rem', sm: '1rem', md: '1.1rem' },
                        }}
                      >
                        {topTeam}
                      </Typography>
                      {topTeamLogoUrl && (
                        <Box sx={{ width: { xs: 20, sm: 26, md: 28 }, flexShrink: 0 }} />
                      )}
                    </Box>
                  </Box>

                  {/* Top Numbers Row(s) */}
                  {hasMultipleNumberSets ? (
                    // Multi-number mode: render a row for each number set with labels at the end
                    // Reverse order so Q1/1H is closest to the grid (inside) and Q4/2H is furthest (outside)
                    [...numberSetKeys].reverse().map((key, index, arr) => {
                      const nums = getTopNumbersForKey(key);
                      const isLastRow = index === arr.length - 1;
                      return (
                        <Box key={key} sx={{ display: 'flex', mb: isLastRow ? 0 : '2px' }}>
                          {/* Empty space for left team label */}
                          <Box sx={{ width: { xs: headerSize.xs, sm: headerSize.sm, md: headerSize.md } }} />
                          {/* Spacer for ALL left number columns (column width + margin) */}
                          <Box sx={{ width: { xs: numberSetKeys.length * (headerSize.xs * 0.8 + 2), sm: numberSetKeys.length * (headerSize.sm * 0.8 + 2), md: numberSetKeys.length * (headerSize.md * 0.8 + 2) } }} />
                          {/* Number cells - for cell modes, render merged header cells with multiple numbers */}
                          {cellMode !== 'standard' ? (
                            Array.from({ length: visualCols }, (_, visualColIndex) => {
                              const numbersInCell = getNumbersForMergedHeaderCell(nums, visualColIndex, colSpan);
                              return (
                                <Box
                                  key={visualColIndex}
                                  sx={{
                                    width: { xs: mergedCellSize.xs.width, sm: mergedCellSize.sm.width, md: mergedCellSize.md.width },
                                    height: { xs: headerSize.xs, sm: headerSize.sm, md: headerSize.md },
                                    display: 'flex',
                                    flexDirection: 'row',
                                    alignItems: 'stretch',
                                    justifyContent: 'stretch',
                                    ml: '2px',
                                  }}
                                >
                                  {board.numbersRevealed ? (
                                    numbersInCell.map((num, i) => (
                                      <Box
                                        key={i}
                                        sx={{
                                          flex: 1,
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          backgroundColor: themeData.headerBackground,
                                          border: `1px solid ${themeData.cellBorder}`,
                                          borderRight: i < numbersInCell.length - 1 ? 'none' : undefined,
                                          borderLeft: i > 0 ? 'none' : undefined,
                                          color: topColor,
                                          fontWeight: themeData.headerFontWeight,
                                          fontFamily: themeData.fontFamily,
                                          fontSize: { xs: '0.65rem', sm: '0.75rem', md: '0.85rem' },
                                        }}
                                      >
                                        {num}
                                      </Box>
                                    ))
                                  ) : (
                                    <Box
                                      sx={{
                                        flex: 1,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        backgroundColor: themeData.headerBackground,
                                        border: `1px solid ${themeData.cellBorder}`,
                                        color: topColor,
                                        fontWeight: themeData.headerFontWeight,
                                        fontFamily: themeData.fontFamily,
                                      }}
                                    >
                                      -
                                    </Box>
                                  )}
                                </Box>
                              );
                            })
                          ) : (
                            nums.map((num, col) => (
                              <Box
                                key={col}
                                sx={{
                                  width: { xs: cellSize.xs, sm: cellSize.sm, md: cellSize.md },
                                  height: { xs: headerSize.xs, sm: headerSize.sm, md: headerSize.md },
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  backgroundColor: themeData.headerBackground,
                                  border: `1px solid ${themeData.cellBorder}`,
                                  color: topColor,
                                  fontWeight: themeData.headerFontWeight,
                                  fontFamily: themeData.fontFamily,
                                  fontSize: { xs: '0.65rem', sm: '0.75rem', md: '0.85rem' },
                                  ml: '2px',
                                }}
                              >
                                {board.numbersRevealed ? num : '-'}
                              </Box>
                            ))
                          )}
                          {/* Key label at the end */}
                          <Box
                            sx={{
                              width: { xs: headerSize.xs * 0.6, sm: headerSize.sm * 0.6, md: headerSize.md * 0.6 },
                              height: { xs: headerSize.xs, sm: headerSize.sm, md: headerSize.md },
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: topColor,
                              fontWeight: 'bold',
                              fontFamily: themeData.fontFamily,
                              fontSize: { xs: '0.5rem', sm: '0.6rem', md: '0.7rem' },
                              ml: '2px',
                            }}
                          >
                            {getShortKeyLabel(key)}
                          </Box>
                        </Box>
                      );
                    })
                  ) : (
                    // Single mode: render single row (with cell mode support)
                    <Box sx={{ display: 'flex', mb: '2px' }}>
                      <Box sx={{ width: { xs: headerSize.xs, sm: headerSize.sm, md: headerSize.md } }} />
                      <Box sx={{ width: { xs: headerSize.xs, sm: headerSize.sm, md: headerSize.md } }} />
                      {cellMode !== 'standard' ? (
                        Array.from({ length: visualCols }, (_, visualColIndex) => {
                          const numbersInCell = getNumbersForMergedHeaderCell(topNumbers, visualColIndex, colSpan);
                          return (
                            <Box
                              key={visualColIndex}
                              sx={{
                                width: { xs: mergedCellSize.xs.width, sm: mergedCellSize.sm.width, md: mergedCellSize.md.width },
                                height: { xs: headerSize.xs, sm: headerSize.sm, md: headerSize.md },
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'stretch',
                                justifyContent: 'stretch',
                                ml: '2px',
                              }}
                            >
                              {board.numbersRevealed ? (
                                numbersInCell.map((num, i) => (
                                  <Box
                                    key={i}
                                    sx={{
                                      flex: 1,
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      backgroundColor: themeData.headerBackground,
                                      border: `1px solid ${themeData.cellBorder}`,
                                      borderRight: i < numbersInCell.length - 1 ? 'none' : undefined,
                                      borderLeft: i > 0 ? 'none' : undefined,
                                      color: topColor,
                                      fontWeight: themeData.headerFontWeight,
                                      fontFamily: themeData.fontFamily,
                                      fontSize: { xs: '0.65rem', sm: '0.75rem', md: '0.85rem' },
                                    }}
                                  >
                                    {num}
                                  </Box>
                                ))
                              ) : (
                                <Box
                                  sx={{
                                    flex: 1,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: themeData.headerBackground,
                                    border: `1px solid ${themeData.cellBorder}`,
                                    color: topColor,
                                    fontWeight: themeData.headerFontWeight,
                                    fontFamily: themeData.fontFamily,
                                  }}
                                >
                                  -
                                </Box>
                              )}
                            </Box>
                          );
                        })
                      ) : (
                        topNumbers.map((num, col) => (
                          <Box
                            key={col}
                            sx={{
                              width: { xs: cellSize.xs, sm: cellSize.sm, md: cellSize.md },
                              height: { xs: headerSize.xs, sm: headerSize.sm, md: headerSize.md },
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: themeData.headerBackground,
                              border: `1px solid ${themeData.cellBorder}`,
                              color: topColor,
                              fontWeight: themeData.headerFontWeight,
                              fontFamily: themeData.fontFamily,
                              fontSize: { xs: '0.65rem', sm: '0.75rem', md: '0.85rem' },
                              ml: '2px',
                            }}
                          >
                            {board.numbersRevealed ? num : '-'}
                          </Box>
                        ))
                      )}
                    </Box>
                  )}

                  {/* Main Grid with Left Team */}
                  <Box sx={{ display: 'flex' }}>
                    {/* Left Team Label */}
                    <Box
                      sx={{
                        width: { xs: headerSize.xs, sm: headerSize.sm, md: headerSize.md },
                        height: cellMode !== 'standard' ? {
                          // Cell mode: height spans Q label row + merged cell rows
                          xs: mergedCellSize.xs.height * visualRows + gap * (visualRows - 1) + (hasMultipleNumberSets ? headerSize.xs * 0.5 + 2 : 0),
                          sm: mergedCellSize.sm.height * visualRows + gap * (visualRows - 1) + (hasMultipleNumberSets ? headerSize.sm * 0.5 + 2 : 0),
                          md: mergedCellSize.md.height * visualRows + gap * (visualRows - 1) + (hasMultipleNumberSets ? headerSize.md * 0.5 + 2 : 0),
                        } : {
                          // Standard mode: height spans Q label row + 10 number rows
                          xs: cellSize.xs * 10 + 20 + (hasMultipleNumberSets ? headerSize.xs * 0.5 + 2 : 0),
                          sm: cellSize.sm * 10 + 20 + (hasMultipleNumberSets ? headerSize.sm * 0.5 + 2 : 0),
                          md: cellSize.md * 10 + 20 + (hasMultipleNumberSets ? headerSize.md * 0.5 + 2 : 0),
                        },
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1,
                      }}
                    >
                      <Typography
                        sx={{
                          fontWeight: 'bold',
                          color: leftColor,
                          fontSize: { xs: '0.85rem', sm: '1rem', md: '1.1rem' },
                          writingMode: 'vertical-rl',
                          textOrientation: 'mixed',
                          transform: 'rotate(180deg)',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {leftTeam}
                      </Typography>
                      {leftTeamLogoUrl && (
                        <Box
                          component="img"
                          src={leftTeamLogoUrl}
                          alt={leftTeam}
                          onError={(e: React.SyntheticEvent<HTMLImageElement>) => { e.currentTarget.style.display = 'none'; }}
                          sx={{ width: { xs: 18, sm: 22, md: 24 }, height: { xs: 18, sm: 22, md: 24 }, objectFit: 'contain', flexShrink: 0 }}
                        />
                      )}
                    </Box>

                    {/* Left Numbers Column(s) */}
                    {hasMultipleNumberSets ? (
                      // Multi-number mode: render a column for each number set with labels
                      // Reverse order so Q1/1H is closest to the grid (inside) and Q4/2H is furthest (outside)
                      [...numberSetKeys].reverse().map((key) => {
                        const nums = getLeftNumbersForKey(key);
                        return (
                          <Box key={key} sx={{ display: 'flex', flexDirection: 'column', mr: 0.25 }}>
                            {/* Key label cell at top of column */}
                            <Box
                              sx={{
                                width: { xs: headerSize.xs * 0.8, sm: headerSize.sm * 0.8, md: headerSize.md * 0.8 },
                                height: { xs: headerSize.xs * 0.5, sm: headerSize.sm * 0.5, md: headerSize.md * 0.5 },
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: leftColor,
                                fontWeight: 'bold',
                                fontFamily: themeData.fontFamily,
                                fontSize: { xs: '0.5rem', sm: '0.6rem', md: '0.7rem' },
                                mb: 0.25,
                              }}
                            >
                              {getShortKeyLabel(key)}
                            </Box>
                            {/* Number cells - for cell modes, render merged header cells with multiple numbers */}
                            {cellMode !== 'standard' ? (
                              Array.from({ length: visualRows }, (_, visualRowIndex) => {
                                const numbersInCell = getNumbersForMergedHeaderCell(nums, visualRowIndex, rowSpan);
                                return (
                                  <Box
                                    key={visualRowIndex}
                                    sx={{
                                      width: { xs: headerSize.xs * 0.8, sm: headerSize.sm * 0.8, md: headerSize.md * 0.8 },
                                      height: { xs: mergedCellSize.xs.height, sm: mergedCellSize.sm.height, md: mergedCellSize.md.height },
                                      display: 'flex',
                                      flexDirection: 'column',
                                      alignItems: 'stretch',
                                      justifyContent: 'stretch',
                                      mb: '2px',
                                    }}
                                  >
                                    {board.numbersRevealed ? (
                                      numbersInCell.map((num, i) => (
                                        <Box
                                          key={i}
                                          sx={{
                                            flex: 1,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            backgroundColor: themeData.headerBackground,
                                            border: `1px solid ${themeData.cellBorder}`,
                                            borderBottom: i < numbersInCell.length - 1 ? 'none' : undefined,
                                            borderTop: i > 0 ? 'none' : undefined,
                                            color: leftColor,
                                            fontWeight: themeData.headerFontWeight,
                                            fontFamily: themeData.fontFamily,
                                            fontSize: { xs: '0.65rem', sm: '0.75rem', md: '0.85rem' },
                                          }}
                                        >
                                          {num}
                                        </Box>
                                      ))
                                    ) : (
                                      <Box
                                        sx={{
                                          flex: 1,
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          backgroundColor: themeData.headerBackground,
                                          border: `1px solid ${themeData.cellBorder}`,
                                          color: leftColor,
                                          fontWeight: themeData.headerFontWeight,
                                          fontFamily: themeData.fontFamily,
                                        }}
                                      >
                                        -
                                      </Box>
                                    )}
                                  </Box>
                                );
                              })
                            ) : (
                              nums.map((num, row) => (
                                <Box
                                  key={row}
                                  sx={{
                                    width: { xs: headerSize.xs * 0.8, sm: headerSize.sm * 0.8, md: headerSize.md * 0.8 },
                                    height: { xs: cellSize.xs, sm: cellSize.sm, md: cellSize.md },
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: themeData.headerBackground,
                                    border: `1px solid ${themeData.cellBorder}`,
                                    color: leftColor,
                                    fontWeight: themeData.headerFontWeight,
                                    fontFamily: themeData.fontFamily,
                                    fontSize: { xs: '0.65rem', sm: '0.75rem', md: '0.85rem' },
                                    mb: '2px',
                                  }}
                                >
                                  {board.numbersRevealed ? num : '-'}
                                </Box>
                              ))
                            )}
                          </Box>
                        );
                      })
                    ) : (
                      // Single mode: render single column (with cell mode support)
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        {cellMode !== 'standard' ? (
                          Array.from({ length: visualRows }, (_, visualRowIndex) => {
                            const numbersInCell = getNumbersForMergedHeaderCell(leftNumbers, visualRowIndex, rowSpan);
                            return (
                              <Box
                                key={visualRowIndex}
                                sx={{
                                  width: { xs: headerSize.xs, sm: headerSize.sm, md: headerSize.md },
                                  height: { xs: mergedCellSize.xs.height, sm: mergedCellSize.sm.height, md: mergedCellSize.md.height },
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'stretch',
                                  justifyContent: 'stretch',
                                  mb: '2px',
                                }}
                              >
                                {board.numbersRevealed ? (
                                  numbersInCell.map((num, i) => (
                                    <Box
                                      key={i}
                                      sx={{
                                        flex: 1,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        backgroundColor: themeData.headerBackground,
                                        border: `1px solid ${themeData.cellBorder}`,
                                        borderBottom: i < numbersInCell.length - 1 ? 'none' : undefined,
                                        borderTop: i > 0 ? 'none' : undefined,
                                        color: leftColor,
                                        fontWeight: themeData.headerFontWeight,
                                        fontFamily: themeData.fontFamily,
                                        fontSize: { xs: '0.65rem', sm: '0.75rem', md: '0.85rem' },
                                      }}
                                    >
                                      {num}
                                    </Box>
                                  ))
                                ) : (
                                  <Box
                                    sx={{
                                      flex: 1,
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      backgroundColor: themeData.headerBackground,
                                      border: `1px solid ${themeData.cellBorder}`,
                                      color: leftColor,
                                      fontWeight: themeData.headerFontWeight,
                                      fontFamily: themeData.fontFamily,
                                    }}
                                  >
                                    -
                                  </Box>
                                )}
                              </Box>
                            );
                          })
                        ) : (
                          leftNumbers.map((num, row) => (
                            <Box
                              key={row}
                              sx={{
                                width: { xs: headerSize.xs, sm: headerSize.sm, md: headerSize.md },
                                height: { xs: cellSize.xs, sm: cellSize.sm, md: cellSize.md },
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: themeData.headerBackground,
                                border: `1px solid ${themeData.cellBorder}`,
                                color: leftColor,
                                fontWeight: themeData.headerFontWeight,
                                fontFamily: themeData.fontFamily,
                                fontSize: { xs: '0.65rem', sm: '0.75rem', md: '0.85rem' },
                                mb: '2px',
                              }}
                            >
                              {board.numbersRevealed ? num : '-'}
                            </Box>
                          ))
                        )}
                      </Box>
                    )}

                    {/* Grid Cells */}
                    <Box>
                      {/* Spacer row for multi-number mode to align with left column labels */}
                      {hasMultipleNumberSets && (
                        <Box sx={{ height: { xs: headerSize.xs * 0.5 + 2, sm: headerSize.sm * 0.5 + 2, md: headerSize.md * 0.5 + 2 } }} />
                      )}
                      {/* For cell modes, render merged cells */}
                      {cellMode !== 'standard' ? (
                        Array.from({ length: visualRows }, (_, visualRowIndex) => (
                          <Box key={visualRowIndex} sx={{ display: 'flex', mb: '2px' }}>
                            {Array.from({ length: visualCols }, (_, visualColIndex) => {
                              // Get the first square position to determine owner
                              const { row: firstRow, col: firstCol } = getFirstSquareForVisualPosition(
                                visualRowIndex,
                                visualColIndex,
                                cellMode,
                                cellShape
                              );
                              const square = grid[firstRow]?.[firstCol];

                              // Sum hit counts for all squares in this cell
                              const cellPositions = getSquarePositionsForVisualCell(
                                visualRowIndex,
                                visualColIndex,
                                cellMode,
                                cellShape
                              );
                              const hitCount = cellPositions.reduce(
                                (sum, pos) => sum + getHitCount(pos.row, pos.col),
                                0
                              );

                              const bgColor = getSquareColor(square, firstRow, firstCol);
                              const isUnassigned = !square?.ownerId && !square?.offlinePlayerId;
                              const textColor = isUnassigned ? '#999999' : getContrastColor(bgColor);

                              // Cell group border style
                              const cellGroupBorderColor = themeData.cellGroupBorderColor || 'rgba(0, 0, 0, 0.5)';
                              const cellGroupBorderWidth = themeData.cellGroupBorderWidth || 3;

                              return (
                                <Paper
                                  key={visualColIndex}
                                  elevation={0}
                                  sx={{
                                    width: { xs: mergedCellSize.xs.width, sm: mergedCellSize.sm.width, md: mergedCellSize.md.width },
                                    height: { xs: mergedCellSize.xs.height, sm: mergedCellSize.sm.height, md: mergedCellSize.md.height },
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: bgColor,
                                    border: `1px solid ${themeData.cellBorder}`,
                                    borderRadius: `${themeData.cellBorderRadius}px`,
                                    ml: '2px',
                                    position: 'relative',
                                    boxShadow: `inset 0 0 0 ${cellGroupBorderWidth}px ${cellGroupBorderColor}`,
                                  }}
                                >
                                  {hitCount > 0 && (
                                    <Box
                                      sx={{
                                        position: 'absolute',
                                        top: 3,
                                        right: 3,
                                        backgroundColor: themeData.winnerBadgeBackground,
                                        color: themeData.winnerBadgeTextColor,
                                        borderRadius: '50%',
                                        width: { xs: 16, sm: 18, md: 20 },
                                        height: { xs: 16, sm: 18, md: 20 },
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: { xs: '0.55rem', sm: '0.6rem', md: '0.65rem' },
                                        fontWeight: 'bold',
                                      }}
                                    >
                                      {hitCount}
                                    </Box>
                                  )}
                                  {(() => {
                                    const { icon, handle } = getSquareLabel(square, firstRow, firstCol);
                                    // Scale font sizes up for larger merged cells
                                    const iconSizeScale = cellMode === 'quad' ? 1.5 : 1.2;
                                    const handleSizeScale = cellMode === 'quad' ? 1.4 : 1.15;
                                    return (
                                      <Box
                                        sx={{
                                          color: textColor,
                                          fontWeight: 'bold',
                                          textAlign: 'center',
                                          overflow: 'hidden',
                                          display: 'flex',
                                          flexDirection: 'column',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          gap: 0,
                                          lineHeight: 1,
                                        }}
                                      >
                                        {icon && <Box sx={{ display: 'flex', fontSize: { xs: `${0.7 * iconSizeScale}rem`, sm: `${0.85 * iconSizeScale}rem`, md: `${1 * iconSizeScale}rem` } }}>{icon}</Box>}
                                        {handle && (
                                          <Typography
                                            sx={{
                                              fontSize: icon
                                                ? { xs: `${0.35 * handleSizeScale}rem`, sm: `${0.4 * handleSizeScale}rem`, md: `${0.45 * handleSizeScale}rem` }
                                                : { xs: `${0.5 * handleSizeScale}rem`, sm: `${0.55 * handleSizeScale}rem`, md: `${0.6 * handleSizeScale}rem` },
                                              fontWeight: 'bold',
                                              lineHeight: 1,
                                              color: 'inherit',
                                            }}
                                          >
                                            {handle}
                                          </Typography>
                                        )}
                                      </Box>
                                    );
                                  })()}
                                </Paper>
                              );
                            })}
                          </Box>
                        ))
                      ) : (
                        grid.map((row, rowIndex) => (
                          <Box key={rowIndex} sx={{ display: 'flex', mb: '2px' }}>
                            {row.map((square, colIndex) => {
                              const hitCount = getHitCount(rowIndex, colIndex);
                              const bgColor = getSquareColor(square, rowIndex, colIndex);
                              const isUnassigned = !square?.ownerId && !square?.offlinePlayerId;
                              const textColor = isUnassigned ? '#999999' : getContrastColor(bgColor);

                              // Get box neighbor border style if enabled
                              const boxNeighborConfig = board?.payoutConfig?.boxNeighborPayouts;
                              const boxBorderStyle = boxNeighborConfig?.enabled
                                ? getBoxBorderStyle(
                                    rowIndex,
                                    colIndex,
                                    boxNeighborConfig.boxSize,
                                    themeData.boxNeighborBorderColor || '#1976d2',
                                    themeData.boxNeighborBorderWidth || 3
                                  )
                                : {};

                              return (
                                <Paper
                                  key={colIndex}
                                  elevation={0}
                                  sx={{
                                    width: { xs: cellSize.xs, sm: cellSize.sm, md: cellSize.md },
                                    height: { xs: cellSize.xs, sm: cellSize.sm, md: cellSize.md },
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: bgColor,
                                    border: `1px solid ${themeData.cellBorder}`,
                                    borderRadius: `${themeData.cellBorderRadius}px`,
                                    ml: '2px',
                                    position: 'relative',
                                    ...boxBorderStyle,
                                  }}
                                >
                                  {hitCount > 0 && (
                                    <Box
                                      sx={{
                                        position: 'absolute',
                                        top: 2,
                                        right: 2,
                                        backgroundColor: themeData.winnerBadgeBackground,
                                        color: themeData.winnerBadgeTextColor,
                                        borderRadius: '50%',
                                        width: { xs: 14, sm: 16, md: 18 },
                                        height: { xs: 14, sm: 16, md: 18 },
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: { xs: '0.5rem', sm: '0.55rem', md: '0.6rem' },
                                        fontWeight: 'bold',
                                      }}
                                    >
                                      {hitCount}
                                    </Box>
                                  )}
                                  {(() => {
                                    const { icon, handle } = getSquareLabel(square, rowIndex, colIndex);
                                    return (
                                      <Box
                                        sx={{
                                          color: textColor,
                                          fontWeight: 'bold',
                                          textAlign: 'center',
                                          overflow: 'hidden',
                                          display: 'flex',
                                          flexDirection: 'column',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          gap: 0,
                                          lineHeight: 1,
                                        }}
                                      >
                                        {icon && <Box sx={{ display: 'flex', fontSize: { xs: '0.7rem', sm: '0.85rem', md: '1rem' } }}>{icon}</Box>}
                                        {handle && (
                                          <Typography
                                            sx={{
                                              fontSize: icon
                                                ? { xs: '0.35rem', sm: '0.4rem', md: '0.45rem' }
                                                : { xs: '0.5rem', sm: '0.55rem', md: '0.6rem' },
                                              fontWeight: 'bold',
                                              lineHeight: 1,
                                              color: 'inherit',
                                            }}
                                          >
                                            {handle}
                                          </Typography>
                                        )}
                                      </Box>
                                    );
                                  })()}
                                </Paper>
                              );
                            })}
                          </Box>
                        ))
                      )}
                    </Box>
                  </Box>
                </Box>
              </Box>

              {/* Info Panel */}
              <Box sx={{
                width: { xs: '100%', md: 300 },
                p: 2,
                backgroundColor: hasTheme
                  ? (isLightText ? 'rgba(0,0,0,0.35)' : 'rgba(255,255,255,0.85)')
                  : 'background.paper',
                borderRadius: 2,
              }}>
                {/* Payouts Table */}
                <Typography sx={{ fontWeight: 'bold', fontSize: '1rem', mb: 1, color: textPrimary }}>
                  Payouts
                </Typography>
                <Box
                  component="table"
                  sx={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    fontSize: '0.85rem',
                    color: textPrimary,
                    '& th, & td': {
                      border: `1px solid ${tableBorder}`,
                      px: 1,
                      py: 0.5,
                      textAlign: 'left',
                    },
                    '& th': {
                      backgroundColor: tableHeader,
                      fontWeight: 'bold',
                    },
                  }}
                >
                  <thead>
                    <tr>
                      <th>Period</th>
                      <th>Main</th>
                      {quarterWrongWayPayouts && Object.values(quarterWrongWayPayouts).some(v => v > 0) && (
                        <th>Wrong Way</th>
                      )}
                      {quarterPlusNPayouts && Object.values(quarterPlusNPayouts).some(v => v > 0) && (
                        <th>+{board.payoutConfig?.plusNOffset ?? 5}</th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {orderedQuarterKeys.map((key) => {
                      const mainAmount = quarterPayouts[key] || 0;
                      const wrongWayAmount = quarterWrongWayPayouts?.[key] || 0;
                      const plusNAmount = quarterPlusNPayouts?.[key] || 0;
                      const hasWrongWayColumn = quarterWrongWayPayouts && Object.values(quarterWrongWayPayouts).some(v => v > 0);
                      const hasPlusNColumn = quarterPlusNPayouts && Object.values(quarterPlusNPayouts).some(v => v > 0);

                      return (
                        <tr key={key}>
                          <td>{getQuarterKeyLabel(key, board.quarters || 4)}</td>
                          <td>${mainAmount.toFixed(0)}</td>
                          {hasWrongWayColumn && <td>{wrongWayAmount > 0 ? `$${wrongWayAmount.toFixed(0)}` : '-'}</td>}
                          {hasPlusNColumn && <td>{plusNAmount > 0 ? `$${plusNAmount.toFixed(0)}` : '-'}</td>}
                        </tr>
                      );
                    })}
                    {board.payoutConfig?.overtimeConfig?.enabled && (
                      <tr>
                        <td>Final (OT)</td>
                        <td>${(board.payoutConfig.overtimeConfig.overtimeAmount ?? 0).toFixed(0)}</td>
                        {quarterWrongWayPayouts && Object.values(quarterWrongWayPayouts).some(v => v > 0) && <td>-</td>}
                        {quarterPlusNPayouts && Object.values(quarterPlusNPayouts).some(v => v > 0) && <td>-</td>}
                      </tr>
                    )}
                  </tbody>
                </Box>

                {/* Custom Payouts */}
                {customCategories && customCategories.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography sx={{ fontWeight: 'bold', fontSize: '0.9rem', mb: 0.5, color: textPrimary }}>
                      Custom Payouts
                    </Typography>
                    {customCategories.map((cat) => (
                      <Typography key={cat.id} sx={{ color: textSecondary, fontSize: '0.85rem' }}>
                        {cat.name}: ${cat.payoutAmount.toFixed(0)}
                        {cat.wrongWayEnabled && ' (2-way)'}
                      </Typography>
                    ))}
                  </Box>
                )}

                {/* Neighbor Payouts */}
                {board.payoutConfig?.neighborPayouts?.enabled && (
                  <Box sx={{ mt: 2 }}>
                    <Typography sx={{ fontWeight: 'bold', fontSize: '0.9rem', mb: 0.5, color: textPrimary }}>
                      Neighbor Payouts
                    </Typography>
                    {(() => {
                      const neighborConfig = board.payoutConfig.neighborPayouts;
                      const directions = neighborConfig.directions || [];
                      const directionLabel = directions.length === 8
                        ? 'All 8 directions'
                        : directions.length === 4
                          ? directions.every(d => ['N', 'E', 'S', 'W'].includes(d))
                            ? 'Cardinal (N, E, S, W)'
                            : directions.every(d => ['NE', 'SE', 'SW', 'NW'].includes(d))
                              ? 'Diagonal (NE, SE, SW, NW)'
                              : directions.join(', ')
                          : directions.join(', ');

                      return (
                        <>
                          <Typography sx={{ color: textSecondary, fontSize: '0.85rem' }}>
                            Directions: {directionLabel}
                          </Typography>
                          {neighborConfig.usePerQuarterAmounts && neighborConfig.quarterAmounts ? (
                            orderedQuarterKeys.map(key => (
                              <Typography key={key} sx={{ color: textSecondary, fontSize: '0.85rem' }}>
                                {getQuarterKeyLabel(key, board.quarters || 4)}: ${neighborConfig.quarterAmounts![key] || 0}/neighbor
                              </Typography>
                            ))
                          ) : (
                            <Typography sx={{ color: textSecondary, fontSize: '0.85rem' }}>
                              ${neighborConfig.globalAmount || 0} per neighbor
                            </Typography>
                          )}
                        </>
                      );
                    })()}
                  </Box>
                )}

                {/* Box Neighbor Payouts */}
                {board.payoutConfig?.boxNeighborPayouts?.enabled && (
                  <Box sx={{ mt: 2 }}>
                    <Typography sx={{ fontWeight: 'bold', fontSize: '0.9rem', mb: 0.5, color: textPrimary }}>
                      Box Neighbor Payouts
                    </Typography>
                    {(() => {
                      const boxConfig = board.payoutConfig.boxNeighborPayouts;
                      const sizeLabel = boxConfig.boxSize === '2x2' ? '2×2 boxes' : boxConfig.boxSize === '5x5' ? '5×5 quadrants' : '5×2 boxes';

                      return (
                        <>
                          <Typography sx={{ color: textSecondary, fontSize: '0.85rem' }}>
                            Box size: {sizeLabel}
                          </Typography>
                          {boxConfig.usePerQuarterAmounts && boxConfig.quarterAmounts ? (
                            orderedQuarterKeys.map(key => (
                              <Typography key={key} sx={{ color: textSecondary, fontSize: '0.85rem' }}>
                                {getQuarterKeyLabel(key, board.quarters || 4)}: ${boxConfig.quarterAmounts![key] || 0}/neighbor
                              </Typography>
                            ))
                          ) : (
                            <Typography sx={{ color: textSecondary, fontSize: '0.85rem' }}>
                              ${boxConfig.globalAmount || 0} per neighbor
                            </Typography>
                          )}
                        </>
                      );
                    })()}
                  </Box>
                )}

                {/* Game Log */}
                {hits.length > 0 && (
                  <>
                    <Divider sx={{ my: 2, borderColor: tableBorder }} />
                    <Typography sx={{ fontWeight: 'bold', fontSize: '1rem', mb: 1, color: textPrimary }}>
                      Game Log
                    </Typography>
                    <Typography sx={{ color: textSecondary, fontSize: '0.85rem', mb: 1 }}>
                      {board.awayTeam} @ {board.homeTeam}
                    </Typography>
                    {hits
                      .filter(hit => !hit.isWrongWay && !hit.isPlusN)
                      .map((hit, idx) => {
                        let label: string;
                        if (hit.categoryId) {
                          const cat = customCategories?.find(c => c.id === hit.categoryId);
                          label = cat?.name || 'Custom';
                        } else if (hit.quarter === 0) {
                          label = 'Final';
                        } else if (board.quarters === 2) {
                          label = hit.quarter === 1 ? '1H' : '2H';
                        } else if (board.quarters === 3) {
                          label = `P${hit.quarter}`;
                        } else {
                          label = `Q${hit.quarter}`;
                        }
                        return (
                          <Typography key={idx} sx={{ color: textSecondary, fontSize: '0.85rem' }}>
                            <strong>{label}:</strong> {hit.awayScore} - {hit.homeScore}
                          </Typography>
                        );
                      })}
                  </>
                )}

                {/* Download Button */}
                <Box sx={{ mt: 3 }}>
                  <BoardExport
                    board={board}
                    grid={grid}
                    hits={hits}
                    buttonText="Download image"
                    themeData={board.theme?.themeData}
                    iconOnly
                    iconColor={textPrimary}
                    hideNames={board.hideNames && (board.status === 'draft' || board.status === 'live')}
                    teamMappings={teamMappings}
                  />
                </Box>
              </Box>
            </Box>
            </Box>
          </ThemeWrapper>
        </Box>
      </Box>

      {/* Footer */}
      <Box
        component="footer"
        sx={{
          py: 2,
          px: 2,
          mt: 'auto',
          backgroundColor: (theme) =>
            theme.palette.mode === 'light' ? theme.palette.grey[200] : theme.palette.grey[800],
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            component="img"
            src={logoSvg}
            alt="SquareUp"
            sx={{ height: 20, opacity: 0.7 }}
          />
          <Typography variant="body2" sx={{ color: 'text.secondary', opacity: 0.7 }}>
            by
          </Typography>
          <Box
            component="a"
            href="https://katapultmedia.com/"
            target="_blank"
            rel="noopener noreferrer"
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <Box
              component="img"
              src={kLogoSvg}
              alt="Katapult"
              sx={{ height: 20, opacity: 0.7, '&:hover': { opacity: 1 } }}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
